import {Injectable} from '@angular/core';
import {Phase, Project, ProjectStatus} from "../interfaces/project";
import {APIService} from "./api.service";
import { HttpParams } from "@angular/common/http";
import {Paged} from "../interfaces/paged";
import {MemberAssignment} from "../interfaces/memberAssignment";
import {Delete} from "../interfaces/colDef";
import {User} from "../interfaces/user";
import {HoursOverviewPreview} from "../interfaces/hoursOverview";
import {Globals} from "./Globals";

export interface ClientChange {
  open_drafts: number;
  uninvoiced_hours: number;
  uninvoiced_trips: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectService extends APIService{

  public hoursOverview(clientId: number, projectId: number, from: Date, to: Date): Promise<HoursOverviewPreview>{
    return this.get<HoursOverviewPreview>(this.hoursOverviewUrl(clientId, projectId, from, to, ''), 'Could not load the hours overview');
  }

  public async downloadHoursOverview(clientId: number, projectId: number, from: Date, to: Date, km: boolean): Promise<void>{
    const response = await this.get<any>(this.hoursOverviewUrl(clientId, projectId, from, to, '/pdf') + (km ? '&km=1' : ''), 'Could not create the PDF', {
      responseType: 'blob',
      observe: 'response',
    });
    const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] || 'hours-overview.pdf';
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(a.href);
  }

  private hoursOverviewUrl(clientId: number, projectId: number, from: Date, to: Date, suffix: string): string{
    return `/clients/${clientId}/projects/${projectId}/hours-overview${suffix}?from=${Globals.toISODate(from)}&to=${Globals.toISODate(to)}`;
  }

  public async getArchivedProjects(){
    const params = new HttpParams().set('status', ProjectStatus.ARCHIVED.toString());
    return await this.get<Project[]>('/projects', 'Could not get Archived Projects', {params});
  }

  public async findProjects(page: number = 1, filters = null, sorts:{col: string, dir: number}[] = []): Promise<Paged<Project>>{
    return await this.post<any, Paged<Project>>('/projects/find', {
      page,
      sorts,
      filters
    });
  }

  public async getAllProjects(){
    return await this.get<Project[]>('/projects?status=' + ProjectStatus.ACTIVE, 'Could not get all Projects');
  }

  public async getProjects(options: { users?:boolean, status?:ProjectStatus, page?:number } = {}, page:number = 1){
    if(options.status === null || options.status === undefined){
      options.status = ProjectStatus.ACTIVE;
    }
    options.page = page;

    return await this.get<Project[]>('/projects', 'Could not get Projects', {params: options});
  }

  public async getProject(id: number){
    if(isNaN(id)){
      return null;
    }
    return await this.get<Project>('/projects/' + id, 'Could not find project: ' + id);
  }

  public async addProject(project: Project): Promise<Project> {
    return await this.post<Project, Project>('/projects/', project, project.name + ' added!');
  }

  public async changeProjectStatus(id: number, status: ProjectStatus = ProjectStatus.ACTIVE) {
    const updates: Partial<Project> = { status };
    return await this.saveProject(id, updates, "Project " + ProjectStatus[status]);
  }

  // The locale picks the language of a refusal, such as approved hours not invoiced yet.
  public async deleteProject(id: number, locale: string) {
    return await this.delete<Delete>('/projects/' + id + '?locale=' + encodeURIComponent(locale), 'Project deleted!');
  }

  // A new billing client with invoicing work pending is refused with a 409 and its counts until confirmClientChange agrees.
  public async saveProject(id: number, updates:Partial<Project>, savemessage = null, confirmClientChange: (change: ClientChange) => Promise<boolean> = null) {
    try {
      return await this.put<Partial<Project>, Project>('/projects/' + id, updates, savemessage, confirmClientChange ? [409] : []);
    } catch (e) {
      const change: ClientChange = e?.status === 409 ? e.error?.client_change : null;
      if (!change || !confirmClientChange) { throw e; }
      return await confirmClientChange(change)
        ? await this.put<Partial<Project> & {confirm_client_change: boolean}, Project>('/projects/' + id, {...updates, confirm_client_change: true}, savemessage)
        : null;
    }
  }

  public async addAssignment(assignment: Partial<MemberAssignment>){
    return await this.post('/assignment', assignment, 'User assigned to project!');
  }

  public async setAssignments(id:number, assignments: {phases: Partial<Phase>[],
      assignments: Partial<MemberAssignment>[]}|Partial<MemberAssignment>[]|Partial<Phase>[]){
    return await this.post<any, Phase[]>('/projects/'+id +'/assignments', assignments, 'Project members updated!');
  }

  async getPhases(id: number){
    return await this.get<Phase[]>('/projects/' + id + '/phases');
  }

  async findSchedules(page: number, filters: any, sort: { col: string; dir: number }) {
    return await this.post<any, Paged<any>>('/projects/schedule', {
      page,
      sort,
      filters
    });
  }

  async findTimeline(buckets, page: number = 1, size: number = 40, filters = null, sort:{col: string, dir: number} = null): Promise<Paged<any>>{
    return await this.post<any, Paged<any>>('/projects/timeline', {
      size,
      page,
      sort,
      buckets,
      filters
    });
  }

  async segments(id: number, from: string|Date, to: string|Date) {
    return await this.post<any, any>('/projects/'+id+'/timeline', {
      from,
      to
    });
  }

  async invitedProjects(page: number, filters: any, sorts: { col: string; dir: number; meta?: any }[]) {
    return await this.post<any, Paged<any>>('/projects/invited', {
      page,
      sorts,
      filters
    });
  }

  async approveAssignment(id: number) {
    return await this.post<null, any>('/assignment/' + id + '/respond/approve', null).catch(() => {
      return null;
    });
  }

  async rejectAssignment(id: number) {
    return await this.post<null, any>('/assignment/' + id + '/respond/reject', null).catch(() => {
      return null;
    });
  }
}
